import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { localDateStr } from '../utils/dateUtils';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import { generateInvoicePDF, generateSingleInvoicePDF, generateProformaInvoicePDF } from '../utils/pdfGenerator';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../context/AuthContext';

const STATUSES = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled', 'on_hold'];
const statusColors = { pending: 'warning', confirmed: 'info', processing: 'info', shipped: 'primary', delivered: 'success', cancelled: 'error', on_hold: 'neutral' };

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'cash',          label: 'Cash' },
  { value: 'upi',           label: 'UPI' },
  { value: 'card',          label: 'Card' },
  { value: 'other',         label: 'Other' },
];

const DownloadIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const EyeIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

function getDueStatus(dueDate) {
  if (!dueDate) return 'Not set';
  const due = new Date(dueDate);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays > 0) return `Due in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
  if (diffDays === 0) return 'Due today';
  const overdueDays = Math.abs(diffDays);
  return `Overdue by ${overdueDays} day${overdueDays === 1 ? '' : 's'}`;
}

// Helper function to extract numeric part from invoice number for proper sorting
function extractInvoiceNumber(invoiceNumber) {
  if (!invoiceNumber) return 0;
  // Extract the last 3 digits from patterns like PL/26-27/051
  const match = invoiceNumber.match(/(\d{3})$/);
  if (match) {
    return parseInt(match[1], 10);
  }
  // Fallback: extract all digits and parse as number
  const digits = invoiceNumber.match(/\d+/);
  return digits ? parseInt(digits[digits.length - 1], 10) : 0;
}

// Sort orders by invoice number in descending order (newest first)
function sortOrdersByInvoiceNumber(orders) {
  return orders.sort((a, b) => {
    const aInvoiceNum = extractInvoiceNumber(a.invoice_number);
    const bInvoiceNum = extractInvoiceNumber(b.invoice_number);
    return bInvoiceNum - aInvoiceNum; // Descending order (newest first)
  });
}

// Component to display due status with payment check
function DueStatusCell({ order }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPayments = async () => {
      try {
        const { data: res } = await api.get('/accounting/payments', { params: { order_id: order.id } });
        setPayments(res.data || []);
      } catch (err) {
        console.error('Failed to load payments:', err);
        setPayments([]);
      } finally {
        setLoading(false);
      }
    };
    loadPayments();
  }, [order.id]);

  // Calculate total paid amount
  const totalPaid = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const orderTotal = Number(order.total || 0);
  const isFullyPaid = totalPaid >= orderTotal && orderTotal > 0;
  const isPartiallyPaid = totalPaid > 0 && totalPaid < orderTotal;

  let status = getDueStatus(order.due_date);
  let color = '#64748b';
  let background = '#f8fafc';

  if (isFullyPaid) {
    // Find the most recent payment date
    const latestPayment = payments.length > 0 
      ? payments.reduce((latest, payment) => {
          const paymentDate = new Date(payment.payment_date);
          const latestDate = new Date(latest.payment_date);
          return paymentDate > latestDate ? payment : latest;
        })
      : null;
    
    if (latestPayment) {
      const paymentDate = new Date(latestPayment.payment_date);
      const formattedDate = paymentDate.toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
      status = `Received (${formattedDate})`;
    } else {
      status = 'Received';
    }
    color = '#166534';
    background = '#ecfdf5';
  } else if (isPartiallyPaid) {
    status = `Partially Paid (${((totalPaid / orderTotal) * 100).toFixed(1)}%)`;
    color = '#92400e';
    background = '#fef3c7';
  } else if (order.due_date) {
    const due = new Date(order.due_date);
    const today = new Date();
    due.setHours(0,0,0,0);
    today.setHours(0,0,0,0);
    if (due < today) {
      color = '#b91c1c';
      background = '#fee2e2';
    } else if (due.getTime() === today.getTime()) {
      color = '#92400e';
      background = '#fef3c7';
    } else {
      color = '#166534';
      background = '#ecfdf5';
    }
  }

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: 999,
      fontSize: '0.78rem',
      fontWeight: 600,
      color,
      background,
      border: '1px solid var(--border)'
    }}>
      {loading ? 'Loading...' : status}
    </span>
  );
}

function DownloadDropdown({ order }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadAndGenerate = async (fn) => {
    setOpen(false);
    const { data: res } = await api.get(`/orders/${order.id}`);
    fn(res.data);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button className="btn btn-ghost btn-icon" data-tooltip="Download PDF"
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <DownloadIcon />
        <ChevronIcon />
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', zIndex: 9999,
          background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 230, padding: '4px 0',
        }}>
          <div style={{ padding: '6px 12px 4px', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Tax Invoice
          </div>
          {[
            { label: 'All 3 Copies (Orig + Dup + Trip)', fn: o => generateInvoicePDF(o, { copies: ['ORIGINAL', 'DUPLICATE', 'TRIPLICATE'] }) },
            { label: 'Original only', fn: o => generateSingleInvoicePDF(o, 'ORIGINAL') },
            { label: 'Duplicate only', fn: o => generateSingleInvoicePDF(o, 'DUPLICATE') },
            { label: 'Triplicate only', fn: o => generateSingleInvoicePDF(o, 'TRIPLICATE') },
          ].map(item => (
            <button key={item.label}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.84rem', color: 'var(--text-primary)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-lighter)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
              onClick={() => loadAndGenerate(item.fn)}>
              {item.label}
            </button>
          ))}
          <div style={{ margin: '4px 12px', borderTop: '1px solid var(--border)' }} />
          <div style={{ padding: '6px 12px 4px', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Proforma Invoice
          </div>
          <button
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.84rem', color: '#c2410c' }}
            onMouseEnter={e => e.currentTarget.style.background = '#fff7ed'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            onClick={() => loadAndGenerate(o => generateProformaInvoicePDF(o))}>
            Download Proforma PDF
          </button>
        </div>
      )}
    </div>
  );
}

function PaymentLinkSection({ order, isPrivileged, onLinkGenerated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [linkUrl, setLinkUrl] = useState(order.payment_link_url || '');
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setLoading(true); setError('');
    try {
      const { data: res } = await api.post(`/orders/${order.id}/payment-link`);
      if (res.success) {
        setLinkUrl(res.payment_link_url);
        if (onLinkGenerated) onLinkGenerated(res.payment_link_url);
      } else {
        setError(res.message || 'Failed to generate payment link.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate payment link.');
    } finally { setLoading(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(linkUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (parseFloat(order.total || 0) <= 0) return null;

  return (
    <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
        Payment Link
      </div>
      {linkUrl ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, fontSize: '0.82rem', color: 'var(--primary)', wordBreak: 'break-all', fontFamily: 'monospace' }}>{linkUrl}</span>
          <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: '0.78rem' }} onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '0.78rem', textDecoration: 'none' }}>
            Open Link
          </a>
        </div>
      ) : isPrivileged ? (
        <div>
          <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '0.82rem' }} onClick={handleGenerate} disabled={loading}>
            {loading ? 'Generating…' : 'Generate Payment Link'}
          </button>
          {error && <span style={{ marginLeft: 10, fontSize: '0.8rem', color: 'var(--error, #dc2626)' }}>{error}</span>}
        </div>
      ) : null}
    </div>
  );
}

function PaymentHistorySection({ orderId, refreshTrigger }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPayments = async () => {
      try {
        const { data: res } = await api.get('/accounting/payments', { params: { order_id: orderId } });
        setPayments(res.data || []);
      } catch (err) {
        console.error('Failed to load payments:', err);
        setPayments([]);
      } finally {
        setLoading(false);
      }
    };
    loadPayments();
  }, [orderId, refreshTrigger]);

  if (loading) {
    return (
      <div style={{ marginTop: 12, padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
        <div className="spinner spinner-sm" style={{ margin: '0 auto 8px' }} />
        Loading payment history...
      </div>
    );
  }

  if (payments.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: 12, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 8 }}>Payment History</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {payments.map(payment => (
          <div key={payment.id} style={{ padding: '8px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.82rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <div>
                <span style={{ fontFamily: 'monospace', color: '#0f172a', fontWeight: 600 }}>{payment.payment_number}</span>
                <span style={{ marginLeft: 8, color: '#64748b' }}>
                  {new Date(payment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </div>
              <div style={{ fontWeight: 700, color: '#0f172a' }}>
                {payment.currency} {Number(payment.amount).toFixed(2)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, fontSize: '0.78rem', color: '#64748b' }}>
              <span>Method: {payment.method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
              {payment.reference && <span>Ref: {payment.reference}</span>}
            </div>
            {payment.notes && (
              <div style={{ marginTop: 4, fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic' }}>
                Notes: {payment.notes}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RecordPaymentSection({ order, onRecorded }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [recordedPayment, setRecordedPayment] = useState(null);
  const today = localDateStr();
  const [form, setForm] = useState({
    amount: Number(order.total || 0).toFixed(2),
    currency: order.currency || 'INR',
    payment_date: today,
    method: 'bank_transfer',
    reference: '',
    notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0) {
      setError('Please enter a valid amount.'); return;
    }
    setSaving(true); setError('');
    try {
      const { data: res } = await api.post('/accounting/payments', {
        order_id:     order.id,
        client_id:    order.client_id,
        amount:       Number(form.amount),
        currency:     form.currency,
        payment_date: form.payment_date,
        method:       form.method,
        reference:    form.reference || undefined,
        notes:        form.notes || undefined,
      });
      setSuccess('Payment recorded successfully.');
      setRecordedPayment(res.data);
      setOpen(false);
      if (onRecorded) onRecorded();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record payment.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      {/* Display recorded payment details */}
      {recordedPayment && (
        <div style={{ marginBottom: 12, padding: '12px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 8 }}>✓ Payment Recorded</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: '0.85rem' }}>
            <div>
              <span style={{ color: '#166534', fontWeight: 600 }}>Payment #:</span>
              <span style={{ marginLeft: 6, color: '#166534', fontFamily: 'monospace' }}>{recordedPayment.payment_number}</span>
            </div>
            <div>
              <span style={{ color: '#166534', fontWeight: 600 }}>Amount:</span>
              <span style={{ marginLeft: 6, color: '#166534', fontWeight: 700 }}>{recordedPayment.currency} {Number(recordedPayment.amount).toFixed(2)}</span>
            </div>
            <div>
              <span style={{ color: '#166534', fontWeight: 600 }}>Date:</span>
              <span style={{ marginLeft: 6, color: '#166534' }}>{new Date(recordedPayment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
            <div>
              <span style={{ color: '#166534', fontWeight: 600 }}>Method:</span>
              <span style={{ marginLeft: 6, color: '#166534' }}>{form.method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
            </div>
            {(recordedPayment.reference || form.reference) && (
              <div style={{ gridColumn: '1 / -1' }}>
                <span style={{ color: '#166534', fontWeight: 600 }}>Reference:</span>
                <span style={{ marginLeft: 6, color: '#166534' }}>{recordedPayment.reference || form.reference}</span>
              </div>
            )}
            {(recordedPayment.notes || form.notes) && (
              <div style={{ gridColumn: '1 / -1' }}>
                <span style={{ color: '#166534', fontWeight: 600 }}>Notes:</span>
                <span style={{ marginLeft: 6, color: '#166534' }}>{recordedPayment.notes || form.notes}</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {success && !recordedPayment && (
        <div style={{ marginBottom: 8, padding: '7px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: '0.83rem', color: '#166534' }}>
          {success}
        </div>
      )}
      
      {!open ? (
        <button className="btn btn-primary" style={{ padding: '7px 16px', fontSize: '0.83rem' }}
          onClick={() => { setOpen(true); setSuccess(''); setRecordedPayment(null); }}>
          Record Payment
        </button>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10 }}>Record Payment</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
            <div>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Amount *</label>
              <input type="number" step="0.01" min="0.01" required className="form-control"
                value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Payment Date *</label>
              <input type="date" required className="form-control"
                value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Method</label>
              <select className="form-control form-select" value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}>
                {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Reference / UTR</label>
              <input type="text" className="form-control" placeholder="e.g. UTR or cheque no."
                value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Notes</label>
              <input type="text" className="form-control" placeholder="Optional notes"
                value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          {error && <div style={{ marginTop: 8, color: '#dc2626', fontSize: '0.8rem' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" style={{ padding: '7px 18px', fontSize: '0.83rem' }} disabled={saving}>
              {saving ? 'Saving…' : 'Save Payment'}
            </button>
            <button type="button" className="btn btn-ghost" style={{ padding: '7px 14px', fontSize: '0.83rem' }} onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function OrderDetailModal({ order: initialOrder, onClose, onLinkGenerated }) {
  const { isPrivileged } = useAuth();
  const [order, setOrder] = useState(initialOrder);
  const [editingDate, setEditingDate] = useState(false);
  const [dateValue, setDateValue] = useState(initialOrder.invoice_date ? initialOrder.invoice_date.split('T')[0] : '');
  const [dateSaving, setDateSaving] = useState(false);
  const [dateError, setDateError] = useState('');
  const [editingDueDate, setEditingDueDate] = useState(false);
  const [dueDateValue, setDueDateValue] = useState(initialOrder.due_date ? initialOrder.due_date.split('T')[0] : '');
  const [dueDateSaving, setDueDateSaving] = useState(false);
  const [dueDateError, setDueDateError] = useState('');
  const [paymentRefreshTrigger, setPaymentRefreshTrigger] = useState(0);

  if (!order) return null;
  const items = order.items || [];
  const hasTax = order.tax_type && order.tax_type !== 'none';
  const taxableAmount = Number(order.subtotal || 0) - Number(order.discount_amount || 0);
  const taxAmt = hasTax ? taxableAmount * (Number(order.tax_rate) || 0) / 100 : 0;
  const grandTotal = taxableAmount + taxAmt;

  const handleSaveDate = async () => {
    if (!dateValue) { setDateError('Please select a date.'); return; }
    setDateSaving(true); setDateError('');
    try {
      const { data: res } = await api.patch(`/orders/${order.id}/invoice-date`, { invoice_date: dateValue });
      setOrder(res.data);
      setEditingDate(false);
    } catch (err) {
      setDateError(err.response?.data?.message || 'Failed to update invoice date.');
    } finally { setDateSaving(false); }
  };

  const handleSaveDueDate = async () => {
    if (!dueDateValue) { setDueDateError('Please select a date.'); return; }
    setDueDateSaving(true); setDueDateError('');
    try {
      const { data: res } = await api.patch(`/orders/${order.id}/due-date`, { due_date: dueDateValue });
      setOrder(res.data);
      setEditingDueDate(false);
    } catch (err) {
      setDueDateError(err.response?.data?.message || 'Failed to update due date.');
    } finally { setDueDateSaving(false); }
  };

  const invoiceDateDisplay = order.invoice_date
    ? new Date(order.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'Not set';

  const dueDateDisplay = order.due_date
    ? new Date(order.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'Not set';

  const invoiceDateField = isPrivileged ? (
    <div>
      {!editingDate ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.88rem' }}>{invoiceDateDisplay}</span>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            style={{ padding: 2, color: 'var(--text-muted)' }}
            title="Change invoice date"
            onClick={() => setEditingDate(true)}
          >
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <input
              type="date"
              className="form-control"
              style={{ fontSize: '0.83rem', padding: '3px 6px', width: 145 }}
              value={dateValue}
              onChange={e => setDateValue(e.target.value)}
            />
            <button
              className="btn btn-primary btn-sm"
              style={{ padding: '3px 10px', fontSize: '0.78rem' }}
              onClick={handleSaveDate}
              disabled={dateSaving}
            >
              {dateSaving ? '…' : 'Save'}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              style={{ padding: '3px 8px', fontSize: '0.78rem' }}
              onClick={() => { setEditingDate(false); setDateError(''); setDateValue(order.invoice_date ? order.invoice_date.split('T')[0] : ''); }}
            >
              Cancel
            </button>
          </div>
          {dateError && <div style={{ marginTop: 4, color: '#dc2626', fontSize: '0.75rem' }}>{dateError}</div>}
        </div>
      )}
    </div>
  ) : invoiceDateDisplay;

  const dueDateField = isPrivileged ? (
    <div>
      {!editingDueDate ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.88rem' }}>{dueDateDisplay}</span>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            style={{ padding: 2, color: 'var(--text-muted)' }}
            title="Change due date"
            onClick={() => setEditingDueDate(true)}
          >
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <input
              type="date"
              className="form-control"
              style={{ fontSize: '0.83rem', padding: '3px 6px', width: 145 }}
              value={dueDateValue}
              onChange={e => setDueDateValue(e.target.value)}
            />
            <button
              className="btn btn-primary btn-sm"
              style={{ padding: '3px 10px', fontSize: '0.78rem' }}
              onClick={handleSaveDueDate}
              disabled={dueDateSaving}
            >
              {dueDateSaving ? '…' : 'Save'}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              style={{ padding: '3px 8px', fontSize: '0.78rem' }}
              onClick={() => { setEditingDueDate(false); setDueDateError(''); setDueDateValue(order.due_date ? order.due_date.split('T')[0] : ''); }}
            >
              Cancel
            </button>
          </div>
          {dueDateError && <div style={{ marginTop: 4, color: '#dc2626', fontSize: '0.75rem' }}>{dueDateError}</div>}
        </div>
      )}
    </div>
  ) : dueDateDisplay;

  return (
    <Modal open onClose={onClose} title={`Tax Invoice — ${order.invoice_number || order.order_number}`} size="lg">
      <div style={{ marginBottom: 10, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe', fontSize: '0.82rem', color: '#1e40af', fontWeight: 600 }}>
        Tax Invoice — official document for GST/tax purposes.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 16 }}>
        {[
          ['Invoice #', order.invoice_number || <span key="inv" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Assigned on delivery</span>],
          ['Order #', order.order_number],
          ['Client', order.client_name],
          ['Status', <span key="s" className={`badge badge-${statusColors[order.status] || 'neutral'}`}>{order.status}</span>],
          ['Quote #', order.quote_number || '—'],
          ['Quote Date', order.quote_date ? new Date(order.quote_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'],
          ['PO Number', order.po_number || '—'],
          ['PO Date', order.po_date ? new Date(order.po_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'],
          ['Currency', order.currency || 'INR'],
          ['Expected Delivery', order.expected_delivery ? new Date(order.expected_delivery).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'],
          ['Invoice Date', invoiceDateField],
          ['Due Date', dueDateField],
          ['GST Number', order.client_gstin || 'Not set'],
          ['Created', new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })],
          ...(hasTax ? [['Tax', `${order.tax_label || order.tax_type?.toUpperCase()} @ ${order.tax_rate}%`]] : []),
        ].map(([label, value]) => (
          <div key={label}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)' }}>{value}</div>
          </div>
        ))}
      </div>
      {order.notes && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          <strong>Notes:</strong> {order.notes}
        </div>
      )}
      <table className="table" style={{ marginBottom: 0 }}>
        <thead><tr><th>#</th><th>Item</th><th>Pack</th><th>Qty</th><th>Price</th><th>Disc%</th><th>Total</th></tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it.id}>
              <td>{i + 1}</td>
              <td style={{ fontWeight: 500 }}>{it.item_name || it.catalog_item_name || '—'}</td>
              <td>{it.pack_size || '—'}</td>
              <td style={{ textAlign: 'center' }}>{it.quantity}</td>
              <td style={{ textAlign: 'right' }}>{order.currency} {Number(it.price).toFixed(2)}</td>
              <td style={{ textAlign: 'center' }}>{it.discount_percent || 0}%</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{order.currency} {Number(it.total).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 12, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Subtotal: <strong>{order.currency} {Number(order.subtotal || 0).toFixed(2)}</strong>
        </span>
        {Number(order.discount_percent) > 0 && (
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Discount ({order.discount_percent}%): <strong>-{order.currency} {Number(order.discount_amount || 0).toFixed(2)}</strong>
          </span>
        )}
        {hasTax && (
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            {order.tax_label || order.tax_type?.toUpperCase()} ({order.tax_rate}%): <strong>{order.currency} {taxAmt.toFixed(2)}</strong>
          </span>
        )}
        <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--primary)' }}>
          Grand Total: {order.currency} {grandTotal.toFixed(2)}
        </span>
      </div>

      {isPrivileged && Number(order.commission) > 0 && (
        <div style={{ marginTop: 6, fontSize: '0.9rem', fontWeight: 600, color: '#f59e0b' }}>
          Commission: {order.currency} {Number(order.commission).toFixed(2)}
        </div>
      )}

      {isPrivileged && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600, color: '#f59e0b' }}>Update Commission:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '0.82rem' }}>{order.currency}</span>
              <input
                type="number"
                step="0.01"
                min="0"
                defaultValue={order.commission || ''}
                placeholder="Enter amount"
                id={`commission-input-${order.id}`}
                style={{ 
                  width: '100px', 
                  padding: '4px 8px', 
                  border: '1px solid var(--border)', 
                  borderRadius: 4, 
                  fontSize: '0.82rem',
                  textAlign: 'right'
                }}
              />
              <button
                onClick={async () => {
                  const input = document.getElementById(`commission-input-${order.id}`);
                  const commissionValue = input.value === '' ? 0 : parseFloat(input.value);
                  
                  if (isNaN(commissionValue) || commissionValue < 0) {
                    alert('Please enter a valid commission amount.');
                    return;
                  }
                  
                  try {
                    await api.patch(`/orders/${order.id}/commission`, { commission: commissionValue });
                    const { data: refreshed } = await api.get(`/orders/${order.id}`);
                    setOrder(refreshed.data);
                    alert('Commission updated successfully.');
                    input.value = '';
                  } catch (err) {
                    console.error('Commission update error:', err);
                    const errorMessage = err.response?.data?.message || err.message || 'Failed to update commission.';
                    alert(`Error: ${errorMessage}`);
                  }
                }}
                style={{
                  padding: '4px 12px',
                  background: 'var(--primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                Enter
              </button>
            </div>
          </div>
        </div>
      )}

      <PaymentLinkSection order={order} isPrivileged={isPrivileged} onLinkGenerated={onLinkGenerated} />
      <PaymentHistorySection orderId={order.id} refreshTrigger={paymentRefreshTrigger} />
      {isPrivileged && <RecordPaymentSection order={order} onRecorded={() => { setPaymentRefreshTrigger(prev => prev + 1); onClose(); }} />}
    </Modal>
  );
}

export default function TaxInvoiceOrders() {
  const { user, isPrivileged } = useAuth();
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [viewOrder, setViewOrder] = useState(null);

  // Assignment + Work Log state
  const [assignModal, setAssignModal] = useState({ open: false, orderId: null, orderNum: '', ownerId: null });
  const [assignments, setAssignments] = useState([]);
  const [workLogs, setWorkLogs] = useState([]);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignTab, setAssignTab] = useState('assignments');
  const [assignForm, setAssignForm] = useState({ user_id: '', access_level: 'read_only', notes: '' });
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [wlForm, setWlForm] = useState({ log_type: 'note', log_text: '' });
  const [wlSaving, setWlSaving] = useState(false);
  const [wlError, setWlError] = useState('');

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Exclude pending orders from Tax Invoice Orders page
      const params = {
        order_type: 'invoice',
        page,
        limit: 15,
        search: debouncedSearch || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      };

      // Only include status filter if it's not 'pending' (pending orders should never show here)
      if (statusFilter && statusFilter !== 'pending') {
        params.status = statusFilter;
      }

      const { data: res } = await api.get('/orders', { params });
      
      // Additional client-side filtering to ensure no pending orders appear
      let filteredOrders = (res.data || []).filter(order => order.status !== 'pending');
      
      // Sort orders by invoice number in sequential order (newest first)
      filteredOrders = sortOrdersByInvoiceNumber(filteredOrders);
      
      const filteredData = {
        ...res,
        data: filteredOrders
      };
      
      setData(filteredData);
    } finally { setLoading(false); }
  }, [page, debouncedSearch, statusFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const openView = async (o) => {
    const { data: res } = await api.get(`/orders/${o.id}`);
    setViewOrder(res.data);
  };

  const openAssignModal = async (o) => {
    setAssignModal({ open: true, orderId: o.id, orderNum: o.order_number, ownerId: o.user_id });
    setAssignTab('assignments');
    setAssignForm({ user_id: '', access_level: 'read_only', notes: '' });
    setAssignError(''); setWlForm({ log_type: 'note', log_text: '' }); setWlError('');
    const [aRes, wlRes, usersRes] = await Promise.all([
      api.get(`/orders/${o.id}/assignments`),
      api.get(`/orders/${o.id}/work-logs`),
      api.get('/users/dropdown').catch(() => ({ data: { data: [] } })),
    ]);
    setAssignments(aRes.data.data || []);
    setWorkLogs(wlRes.data.data || []);
    setAssignUsers(usersRes.data.data || []);
  };

  const handleAddAssignment = async () => {
    if (!assignForm.user_id) { setAssignError('Please select a user.'); return; }
    setAssignSaving(true); setAssignError('');
    try {
      await api.post(`/orders/${assignModal.orderId}/assignments`, assignForm);
      const { data: r } = await api.get(`/orders/${assignModal.orderId}/assignments`);
      setAssignments(r.data || []);
      setAssignForm({ user_id: '', access_level: 'read_only', notes: '' });
    } catch (err) { setAssignError(err.response?.data?.message || 'Failed to add assignment.'); }
    finally { setAssignSaving(false); }
  };

  const handleUpdateAssignment = async (assignId, access_level) => {
    try {
      await api.put(`/orders/${assignModal.orderId}/assignments/${assignId}`, { access_level });
      setAssignments(prev => prev.map(a => a.id === assignId ? { ...a, access_level } : a));
    } catch (err) { setAssignError(err.response?.data?.message || 'Failed to update assignment.'); }
  };

  const handleRemoveAssignment = async (assignId, userName) => {
    if (!window.confirm(`Remove ${userName} from this order?`)) return;
    try {
      await api.delete(`/orders/${assignModal.orderId}/assignments/${assignId}`);
      setAssignments(prev => prev.filter(a => a.id !== assignId));
    } catch (err) { setAssignError(err.response?.data?.message || 'Failed to remove assignment.'); }
  };

  const handleAddWorkLog = async () => {
    if (!wlForm.log_text.trim()) { setWlError('Please enter a log entry.'); return; }
    setWlSaving(true); setWlError('');
    try {
      await api.post(`/orders/${assignModal.orderId}/work-logs`, wlForm);
      const { data: r } = await api.get(`/orders/${assignModal.orderId}/work-logs`);
      setWorkLogs(r.data || []);
      setWlForm({ log_type: 'note', log_text: '' });
    } catch (err) { setWlError(err.response?.data?.message || 'Failed to add work log.'); }
    finally { setWlSaving(false); }
  };

  const handleDeleteWorkLog = async (logId) => {
    if (!window.confirm('Delete this work log?')) return;
    try {
      await api.delete(`/orders/${assignModal.orderId}/work-logs/${logId}`);
      setWorkLogs(prev => prev.filter(l => l.id !== logId));
    } catch (err) { setWlError(err.response?.data?.message || 'Failed to delete work log.'); }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tax Invoice Orders</h1>
          <p className="page-subtitle">View, download and share tax invoice documents</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ padding: '5px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700, color: '#1e40af' }}>
            TAX INVOICE
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="search-input-wrap" style={{ flex: '1 1 220px' }}>
              <span className="search-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              </span>
              <input className="form-control" placeholder="Search invoice # or client…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-control form-select" style={{ width: 150 }} value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {STATUSES.filter(s => s !== 'pending').map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            <input type="date" className="form-control" style={{ width: 150 }} value={fromDate}
              onChange={e => { setFromDate(e.target.value); setPage(1); }} title="From date" />
            <input type="date" className="form-control" style={{ width: 150 }} value={toDate}
              onChange={e => { setToDate(e.target.value); setPage(1); }} title="To date" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'visible' }}>
        <div className="table-container" style={{ overflowX: 'auto', overflowY: 'visible' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Invoice Date</th>
                <th>Due Date</th>
                <th>Due Status</th>
                <th>Client</th>
                <th>Amount</th>
                {isPrivileged && <th>Commission</th>}
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isPrivileged ? 9 : 8}><div className="loading-overlay"><div className="spinner" /></div></td></tr>
              )}
              {!loading && data.data.length === 0 && (
                <tr><td colSpan={isPrivileged ? 9 : 8}><div className="empty-state"><p>No tax invoice orders found.</p></div></td></tr>
              )}
              {!loading && data.data.map(o => (
                <tr key={o.id}>
                  <td style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.82rem' }}>
                    {o.invoice_number
                      ? o.invoice_number
                      : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontWeight: 400 }}>N/A</span>
                    }
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                    {o.invoice_date
                      ? new Date(o.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                      : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not set</span>
                    }
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                    {o.due_date
                      ? new Date(o.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                      : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not set</span>
                    }
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <DueStatusCell order={o} />
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{o.client_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{o.client_country || ''}</div>
                  </td>
                  <td style={{ fontWeight: 700 }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{o.currency} </span>
                    {Number(o.total || 0).toFixed(2)}
                  </td>
                  {isPrivileged && (
                    <td style={{ fontWeight: 600, color: '#f59e0b' }}>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{o.currency} </span>
                      {Number(o.commission || 0).toFixed(2)}
                    </td>
                  )}
                  <td>
                    <span className={`badge badge-${statusColors[o.status] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>
                      {o.status}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-icon" data-tooltip="View Details" onClick={() => openView(o)}>
                        <EyeIcon />
                      </button>
                      <DownloadDropdown order={o} />
                      <button
                        className="btn btn-ghost btn-icon"
                        data-tooltip="Share on WhatsApp"
                        style={{ color: '#25d366' }}
                        onClick={() => {
                          const amt = `${o.currency} ${Number(o.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                          const recipientName = o.client_name || 'Customer';
                          const msg = `Dear ${recipientName}, please find your invoice #${o.invoice_number || o.order_number} from PRASI LABS. Amount: ${amt}. View/download on request.`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      </button>
                      <button className="btn btn-ghost btn-icon" data-tooltip="Assignments & Work Logs" style={{ color: 'var(--primary)' }} onClick={() => openAssignModal(o)}>
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
      </div>

      {viewOrder && (
        <OrderDetailModal
          order={viewOrder}
          onClose={() => {
            setViewOrder(null);
            load(); // Refresh the main table data
          }}
          onLinkGenerated={(url) => setViewOrder(prev => prev ? { ...prev, payment_link_url: url } : prev)}
        />
      )}

      {/* ── Assignments & Work Logs Modal ─────────────────────── */}
      <Modal open={assignModal.open} onClose={() => setAssignModal({ open: false, orderId: null, orderNum: '', ownerId: null })}
        title={`Assignments — ${assignModal.orderNum || ''}`} size="md"
        footer={<button className="btn btn-secondary" onClick={() => setAssignModal({ open: false, orderId: null, orderNum: '', ownerId: null })}>Close</button>}
      >
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
          {['assignments', 'worklogs'].map(tab => (
            <button key={tab} onClick={() => setAssignTab(tab)}
              style={{ padding: '7px 18px', background: 'none', border: 'none', borderBottom: assignTab === tab ? '2px solid var(--primary)' : '2px solid transparent', fontWeight: assignTab === tab ? 700 : 400, color: assignTab === tab ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>
              {tab === 'assignments' ? 'Assignments' : 'Work Logs'}
            </button>
          ))}
        </div>

        {assignTab === 'assignments' && (
          <div>
            {assignments.length === 0
              ? <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '10px 0 16px' }}>No assignees yet.</div>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {assignments.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{a.user_name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{a.user_role} · Assigned by {a.assigned_by_name || '—'} · {new Date(a.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                      {a.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2, fontStyle: 'italic' }}>{a.notes}</div>}
                    </div>
                    {(isPrivileged || user?.id === assignModal.ownerId)
                      ? <select className="form-control form-select" style={{ fontSize: '0.78rem', width: 120, padding: '3px 6px' }} value={a.access_level} onChange={e => handleUpdateAssignment(a.id, e.target.value)}>
                        <option value="read_only">Read Only</option>
                        <option value="read_write">Read + Write</option>
                      </select>
                      : <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{a.access_level === 'read_write' ? 'Read + Write' : 'Read Only'}</span>
                    }
                    {(isPrivileged || user?.id === assignModal.ownerId) && (
                      <button className="btn btn-ghost btn-icon btn-sm" data-tooltip="Remove" style={{ color: 'var(--error)' }} onClick={() => handleRemoveAssignment(a.id, a.user_name)}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            }
            {(isPrivileged || user?.id === assignModal.ownerId || assignments.some(a => a.user_id === user?.id && a.access_level === 'read_write')) && (
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: 14, border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, fontSize: '0.83rem', marginBottom: 10 }}>Add Assignee</div>
                {assignError && <div className="alert alert-error" style={{ marginBottom: 10, fontSize: '0.8rem', padding: '6px 10px' }}>{assignError}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0, flex: 2, minWidth: 150 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>User</label>
                    <select className="form-control form-select" style={{ fontSize: '0.83rem' }} value={assignForm.user_id} onChange={e => setAssignForm(p => ({ ...p, user_id: e.target.value }))}>
                      <option value="">— Select user —</option>
                      {assignUsers.filter(u => u.id !== assignModal.ownerId && !assignments.some(a => a.user_id === u.id)).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 130 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Access</label>
                    <select className="form-control form-select" style={{ fontSize: '0.83rem' }} value={assignForm.access_level} onChange={e => setAssignForm(p => ({ ...p, access_level: e.target.value }))}>
                      <option value="read_only">Read Only</option>
                      <option value="read_write">Read + Write</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, flex: 2, minWidth: 150 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Note (optional)</label>
                    <input className="form-control" style={{ fontSize: '0.83rem' }} placeholder="e.g. Handling dispatch…" value={assignForm.notes} onChange={e => setAssignForm(p => ({ ...p, notes: e.target.value }))} />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={handleAddAssignment} disabled={assignSaving} style={{ whiteSpace: 'nowrap', marginBottom: 2 }}>{assignSaving ? 'Assigning…' : 'Assign'}</button>
                </div>
              </div>
            )}
          </div>
        )}

        {assignTab === 'worklogs' && (
          <div>
            {(isPrivileged || user?.id === assignModal.ownerId || assignments.some(a => a.user_id === user?.id && a.access_level === 'read_write')) && (
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: 14, border: '1px solid var(--border)', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '0.83rem', marginBottom: 10 }}>Add Work Log</div>
                {wlError && <div className="alert alert-error" style={{ marginBottom: 10, fontSize: '0.8rem', padding: '6px 10px' }}>{wlError}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: 130 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Type</label>
                    <select className="form-control form-select" style={{ fontSize: '0.83rem' }} value={wlForm.log_type} onChange={e => setWlForm(p => ({ ...p, log_type: e.target.value }))}>
                      {['note', 'call', 'meeting', 'email', 'visit', 'follow_up', 'other'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1).replace('_', ' ')}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Entry *</label>
                    <input className="form-control" style={{ fontSize: '0.83rem' }} placeholder="Describe what was done…" value={wlForm.log_text} onChange={e => setWlForm(p => ({ ...p, log_text: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddWorkLog(); } }} />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={handleAddWorkLog} disabled={wlSaving} style={{ whiteSpace: 'nowrap', marginBottom: 2 }}>{wlSaving ? 'Saving…' : 'Log'}</button>
                </div>
              </div>
            )}
            {workLogs.length === 0
              ? <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '10px 0' }}>No work logs yet.</div>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {workLogs.map(log => (
                  <div key={log.id} style={{ display: 'flex', gap: 10, padding: '9px 12px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)', alignItems: 'flex-start' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary-lighter)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase' }}>{(log.user_name || '?')[0]}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>{log.user_name}</span>
                        <span style={{ fontSize: '0.72rem', padding: '1px 7px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{log.log_type.replace('_', ' ')}</span>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{new Date(log.logged_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div style={{ fontSize: '0.83rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log.log_text}</div>
                    </div>
                    {(isPrivileged || log.user_id === user?.id || user?.id === assignModal.ownerId) && (
                      <button className="btn btn-ghost btn-icon btn-sm" data-tooltip="Delete" style={{ color: 'var(--error)', flexShrink: 0 }} onClick={() => handleDeleteWorkLog(log.id)}>
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            }
          </div>
        )}
      </Modal>
    </div>
  );
}
